type SentryRequestLike = {
  data?: unknown;
  headers?: Record<string, unknown>;
  cookies?: unknown;
  query_string?: unknown;
  url?: string;
};

export type SentryEventLike = {
  breadcrumbs?: Array<{
    data?: Record<string, unknown>;
  }>;
  contexts?: Record<string, unknown>;
  event_id?: string;
  request?: SentryRequestLike;
  tags?: Record<string, unknown>;
  transaction?: string;
  spans?: SentrySpanLike[];
  sdkProcessingMetadata?: Record<string, unknown>;
};

export type SentrySpanLike = {
  data?: Record<string, unknown>;
  description?: string;
};

const URL_VALUE_KEYS = [
  "description",
  "from",
  "http.url",
  "previous",
  "to",
  "url",
  "url.full",
] as const;

const QUERY_VALUE_KEYS = [
  "http.query",
  "server.address.query",
  "url.query",
] as const;

export function stripUrlQuery(value: unknown): unknown {
  if (typeof value !== "string" || !/[?#]/.test(value)) return value;
  // HTTP span descriptions carry an operation prefix, not just a URL.
  const operation = value.match(/^([A-Z]+\s+)(.+)$/);
  if (operation) return `${operation[1]}${stripUrlQuery(operation[2])}`;
  try {
    const parsed = new URL(value, "https://eclat.local");
    return /^(?:https?:)?\/\//i.test(value)
      ? `${parsed.origin}${parsed.pathname}`
      : parsed.pathname;
  } catch {
    return value.split(/[?#]/, 1)[0];
  }
}

function scrubUrlFields(record: Record<string, unknown>): void {
  for (const key of URL_VALUE_KEYS) {
    if (key in record) record[key] = stripUrlQuery(record[key]);
  }
  for (const key of QUERY_VALUE_KEYS) delete record[key];
}

function isSearchRequest(url: string | undefined): boolean {
  if (!url) return false;
  const pathname = String(stripUrlQuery(url));
  return (
    pathname === "/blog" ||
    pathname.endsWith("/blog") ||
    pathname.endsWith("/api/search/results") ||
    pathname.endsWith("/api/search/suggest")
  );
}

export function scrubSentryEvent<T extends SentryEventLike>(event: T): T {
  const requestUrl = event.request?.url;
  if (event.request) {
    event.request.url = stripUrlQuery(requestUrl) as string | undefined;
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.headers) {
      for (const key of Object.keys(event.request.headers)) {
        if (
          /^(authorization|proxy-authorization|cookie|set-cookie|x-api-key)$/i.test(
            key
          )
        ) {
          delete event.request.headers[key];
        } else if (/^(referer|referrer)$/i.test(key)) {
          event.request.headers[key] = stripUrlQuery(
            event.request.headers[key]
          );
        }
      }
    }
    if (isSearchRequest(requestUrl)) delete event.request.data;
  }

  event.transaction = stripUrlQuery(event.transaction) as string | undefined;
  if (event.tags) scrubUrlFields(event.tags);

  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.data) scrubUrlFields(breadcrumb.data);
  }

  for (const context of Object.values(event.contexts ?? {})) {
    if (context && typeof context === "object" && !Array.isArray(context)) {
      const fields = context as Record<string, unknown>;
      scrubUrlFields(fields);
      if (
        fields.data &&
        typeof fields.data === "object" &&
        !Array.isArray(fields.data)
      ) {
        scrubUrlFields(fields.data as Record<string, unknown>);
      }
    }
  }

  // The SDK copies this metadata into envelope headers after beforeSend runs.
  const sampling = event.sdkProcessingMetadata?.dynamicSamplingContext;
  if (sampling && typeof sampling === "object") {
    const fields = sampling as Record<string, unknown>;
    fields.transaction = stripUrlQuery(fields.transaction);
  }
  for (const span of event.spans ?? []) scrubSentrySpan(span);

  return event;
}

export function scrubSentrySpan<T extends SentrySpanLike>(span: T): T {
  span.description = stripUrlQuery(span.description) as string | undefined;
  if (span.data) scrubUrlFields(span.data);
  return span;
}
